import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import MermaidDiagram from './MermaidDiagram';
import '../styles/LessonPlanPreview.css';

// ── helpers ──────────────────────────────────────────────────────────────────

function typeBadgeClass(type) {
  if (!type) return '';
  const t = type.toLowerCase().replace(/[^a-z-]/g, '');
  if (t === 'mcq') return 'mcq';
  if (t === 'short-answer' || t === 'shortanswer') return 'short-answer';
  if (t === 'counterfactual') return 'counterfactual';
  return '';
}

function typeBadgeLabel(type) {
  if (!type) return type;
  const t = type.toLowerCase();
  if (t === 'mcq') return 'MCQ';
  if (t === 'short-answer') return 'Short Answer';
  if (t === 'counterfactual') return 'Counterfactual';
  return type;
}

function truncateText(text, max = 140) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

// Normalize any "pp. A–B" / "pp. A-B" page range inside a string so the lower
// page always comes first — section data and the LLM occasionally emit an
// inverted range like "pp. 7–6". A single-page range collapses to "p. N".
export function fixPageRanges(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\bpp?\.\s*(\d+)\s*[–—-]\s*(\d+)/g, (_m, a, b) => {
    const lo = Math.min(Number(a), Number(b));
    const hi = Math.max(Number(a), Number(b));
    return lo === hi ? `p. ${lo}` : `pp. ${lo}–${hi}`;
  });
}

// ── Highlights (per cycle/question) ───────────────────────────────────────────
// An instructor can pin one or more exact PDF passages to a cycle/question:
// select text in the open PDF, then click the 🖍﹢ button on that item. Each
// highlight is stored as { page, text }. The cycle author LLM also suggests its
// own passages in `ai_highlights`. Students see the instructor's highlights when
// any exist, otherwise the AI's — instructor highlights always override.

// Normalize an item's highlights to an array (accepts legacy single `highlight`).
function getHighlights(item) {
  if (Array.isArray(item?.highlights)) return item.highlights.filter(h => h && h.text);
  if (item?.highlight?.text) return [item.highlight];
  return [];
}

// AI-suggested highlights (emitted by the cycle author LLM).
function getAiHighlights(item) {
  return Array.isArray(item?.ai_highlights) ? item.ai_highlights.filter(h => h && h.text) : [];
}

// Renders an item's highlights — AI-suggested (purple) first, then
// instructor-pinned — with optional per-chip remove buttons.
function HighlightList({ highlights, aiHighlights = [], onRemove, onRemoveAi }) {
  const hasManual = highlights.length > 0;
  if (!hasManual && !aiHighlights.length) return null;
  return (
    <div className="item-highlight-list">
      {aiHighlights.map((h, i) => (
        <div key={`ai-${i}`} className="item-highlight-chip item-highlight-chip--ai" title={h.text}>
          <span className="item-highlight-ai-badge">AI</span>
          <span className="item-highlight-page">p.{h.page ?? '?'}</span>
          <span className="item-highlight-text">&ldquo;{truncateText(h.text)}&rdquo;</span>
          {onRemoveAi && (
            <button type="button" className="highlight-clear-btn" onClick={() => onRemoveAi(i)} title="Remove this AI highlight">×</button>
          )}
        </div>
      ))}
      {highlights.map((h, i) => (
        <div key={`manual-${i}`} className="item-highlight-chip" title={h.text}>
          <span className="item-highlight-icon">🖍</span>
          <span className="item-highlight-page">p.{h.page ?? '?'}</span>
          <span className="item-highlight-text">&ldquo;{truncateText(h.text)}&rdquo;</span>
          {onRemove && (
            <button type="button" className="highlight-clear-btn" onClick={() => onRemove(i)} title="Remove this highlight">×</button>
          )}
        </div>
      ))}
    </div>
  );
}

// Header button: appends the current PDF selection to an item's highlights.
function AddHighlightButton({ selectedText, onAdd }) {
  const hasSel = !!(selectedText && selectedText.trim());
  const [tooltipPos, setTooltipPos] = useState(null);
  const wrapRef = useRef(null);

  const showTooltip = () => {
    if (!hasSel && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setTooltipPos({ top: r.top, left: r.left });
    }
  };

  return (
    <span
      ref={wrapRef}
      className="highlight-add-btn-wrap"
      onMouseEnter={showTooltip}
      onMouseLeave={() => setTooltipPos(null)}
    >
      <button
        type="button"
        className="highlight-add-btn"
        disabled={!hasSel}
        onClick={(e) => { e.stopPropagation(); if (hasSel) onAdd(); }}
      >
        🖍﹢
      </button>
      {tooltipPos && !hasSel && ReactDOM.createPortal(
        <div
          className="highlight-add-tooltip"
          role="tooltip"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          Select text in the PDF to attach it as a highlight
        </div>,
        document.body
      )}
    </span>
  );
}

// ── Export helpers ────────────────────────────────────────────────────────────

function buildPrintHtml(plan, paperName) {
  const title = plan.plan_type === 'testing' ? 'Assessment Questions' : 'Reading Cycles Plan';
  let body = '';

  if (plan.plan_type === 'cycles' && Array.isArray(plan.cycles)) {
    plan.cycles.forEach((c, ci) => {
      body += `<h2>Cycle ${c.number ?? ci + 1}: ${c.title || ''}</h2>`;
      if (c.read_sections) body += `<p><strong>Read:</strong> ${fixPageRanges(c.read_sections)}</p>`;
      if (Array.isArray(c.core_ideas) && c.core_ideas.length) {
        body += `<h3>Core Ideas</h3><ol>${c.core_ideas.map(i => `<li>${i}</li>`).join('')}</ol>`;
      }
      if (Array.isArray(c.recommendations) && c.recommendations.length) {
        body += `<h3>Guiding Recommendations</h3><ul>${c.recommendations.map(r => `<li>${r}</li>`).join('')}</ul>`;
      }
      if (Array.isArray(c.questions) && c.questions.length) {
        body += `<h3>Questions</h3>`;
        c.questions.forEach((q, qi) => {
          body += `<p><strong>Q${q.number ?? qi + 1}:</strong> ${q.question}</p>`;
          if (Array.isArray(q.options)) body += `<ul>${q.options.map(o => `<li>${o}</li>`).join('')}</ul>`;
          if (q.answer) body += `<p><em>Answer: ${q.answer}</em></p>`;
        });
      }
    });
  } else if (plan.plan_type === 'testing' && Array.isArray(plan.questions)) {
    plan.questions.forEach((q, qi) => {
      body += `<h3>Q${q.number ?? qi + 1} <span style="font-size:12px;color:#666">[${q.type || ''}]</span></h3>`;
      body += `<p>${q.question}</p>`;
      if (Array.isArray(q.options)) body += `<ul>${q.options.map(o => `<li>${o}</li>`).join('')}</ul>`;
      if (q.answer) body += `<p><em>Answer: ${q.answer}</em></p>`;
      if (Array.isArray(q.hints) && q.hints.length) {
        body += `<p><strong>Hints:</strong></p><ol>${q.hints.map(h => `<li>${h}</li>`).join('')}</ol>`;
      }
    });
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title} — ${paperName || 'PaperBuddy'}</title>
<style>
  body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:0 24px;color:#111;line-height:1.6}
  h1{font-size:22px;border-bottom:2px solid #333;padding-bottom:8px}
  h2{font-size:17px;margin-top:32px;color:#333}
  h3{font-size:14px;color:#555;margin-top:16px}
  li{margin-bottom:4px}
  em{color:#555}
  @media print{body{margin:20px}}
</style></head><body>
<h1>${title}</h1>
<p style="color:#888;font-size:13px">${paperName || ''}</p>
${body}
</body></html>`;
}

export function exportPdf(plan, paperName) {
  const html = buildPrintHtml(plan, paperName);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

export function exportWord(plan, paperName) {
  const html = buildPrintHtml(plan, paperName);
  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(paperName || 'plan').replace(/\s+/g, '_')}_plan.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportJson(plan, paperName) {
  const blob = new Blob([JSON.stringify(plan, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(paperName || 'plan').replace(/\s+/g, '_')}_plan.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── QuestionRow ───────────────────────────────────────────────────────────────

function QuestionRow({ question, index, showTypeBadge, onEdit, onRegenerate, onRemove, actionsInBody, regenLoading, readOnly, selectedText, selectedPage }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [regenNotes, setRegenNotes] = useState('');
  const [showRegen, setShowRegen] = useState(false);
  const [hlTooltipPos, setHlTooltipPos] = useState(null);
  const hlBtnRef = useRef(null);

  const hasHints = Array.isArray(question.hints) && question.hints.length > 0;
  const hasOptions = Array.isArray(question.options) && question.options.length > 0;

  function isCorrectOption(option) {
    if (!question.answer) return false;
    const prefixMatch = option.trim().match(/^([A-Z])\./);
    if (!prefixMatch) return false;
    const optLetter = prefixMatch[1];
    const correctLetters = question.answer.toUpperCase().split(/[,\s]+/).filter(l => /^[A-Z]$/.test(l));
    if (correctLetters.length === 0 || correctLetters.length >= (question.options?.length ?? 4)) return false;
    return correctLetters.includes(optLetter);
  }

  const startEdit = (e) => {
    e?.stopPropagation();
    setEditDraft({ ...question, options: question.options ? [...question.options] : [], hints: question.hints ? [...question.hints] : [] });
    setEditing(true);
    setOpen(true);
  };

  const saveEdit = () => {
    onEdit?.(index, editDraft);
    setEditing(false);
  };

  const startRegen = (e) => {
    e?.stopPropagation();
    setShowRegen(true);
    setOpen(true);
  };

  // Append the current PDF selection to this question's highlights (one click,
  // no edit mode). Migrates any legacy single `highlight` into the array.
  const addHighlight = () => {
    const t = (selectedText || '').trim();
    if (!t) return;
    const next = [...getHighlights(question), { page: selectedPage ?? null, text: t }];
    const { highlight, ...rest } = question;
    onEdit?.(index, { ...rest, highlights: next });
    setOpen(true);
  };

  const removeHighlight = (i) => {
    const next = getHighlights(question).filter((_, j) => j !== i);
    const { highlight, ...rest } = question;
    onEdit?.(index, { ...rest, highlights: next });
  };

  const removeAiHighlight = (i) => {
    const next = getAiHighlights(question).filter((_, j) => j !== i);
    onEdit?.(index, { ...question, ai_highlights: next });
  };

  // When the instructor has text selected in the PDF, clicking the question itself
  // attaches that selection as a pinned highlight (no need to hunt for the button).
  // Otherwise the header just expands/collapses as usual.
  const armed = !!onEdit && !readOnly && !!(selectedText && selectedText.trim());

  return (
    <div className={`question-item${regenLoading ? ' question-item--loading' : ''}${armed ? ' question-item--armed' : ''}`}>
      <div className="question-header-wrap">
        <button
          className="question-header"
          onClick={() => { if (armed) addHighlight(); else setOpen(o => !o); }}
          aria-expanded={open}
          title={armed ? 'Click to attach the selected PDF text as a highlight' : undefined}
        >
          <div className="question-header-top">
            <span className="question-num">Q{question.number ?? index + 1}</span>
            {showTypeBadge && question.type && (
              <span className={`question-type-badge ${typeBadgeClass(question.type)}`}>
                {typeBadgeLabel(question.type)}
              </span>
            )}
            {showTypeBadge && Array.isArray(question.sections) && question.sections.length > 0 && (
              <span className="question-section-badge">
                {question.sections.length === 1 && String(question.sections[0]).toLowerCase() === 'all'
                  ? 'All Sections'
                  : question.sections.map(s => `§${s}`).join(', ')}
              </span>
            )}
          </div>
          <span className="question-text">{question.question}</span>
        </button>
        {!actionsInBody && !readOnly && !regenLoading && (onEdit || onRegenerate || onRemove) && (
          <div className="section-actions">
            {onEdit && <AddHighlightButton selectedText={selectedText} onAdd={addHighlight} />}
            {onEdit && <button className="section-action-btn" title="Edit" onClick={e => { e.stopPropagation(); startEdit(); }}>✎</button>}
            {onRegenerate && <button className="section-action-btn section-action-btn--regen" title="Regenerate" onClick={e => { e.stopPropagation(); startRegen(); }}>↺</button>}
            {onRemove && <button className="section-action-btn section-action-btn--remove" title="Remove question" onClick={e => { e.stopPropagation(); onRemove(); }}>✕</button>}
          </div>
        )}
        {regenLoading && <span className="regen-spinner">⟳</span>}
        <button
          type="button"
          className="question-chevron"
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-label={open ? 'Collapse question' : 'Expand question'}
        >{open ? '▲' : '▼'}</button>
      </div>

      {open && !editing && (
        <div className="question-body">
          {hasOptions && (
            <div className="mcq-options">
              {question.options.map((opt, i) => (
                <div key={i} className={`mcq-option${isCorrectOption(opt) ? ' correct' : ''}`}>{opt}</div>
              ))}
            </div>
          )}
          {hasHints && (
            <div className="hints-list">
              {question.hints.map((hint, i) => (
                <div key={i} className="hint-item"><span className="hint-label">Hint {i + 1}:</span>{hint}</div>
              ))}
            </div>
          )}
          {question.answer != null && (
            <div className="question-answer"><span className="answer-label">Answer:</span>{question.answer}</div>
          )}
          {actionsInBody && !readOnly && !regenLoading && (onEdit || onRegenerate || onRemove) && (
            <div className="question-body-action-bar">
              {onEdit && (
                <span
                  ref={hlBtnRef}
                  className="highlight-add-btn-wrap"
                  onMouseEnter={() => {
                    if (!(selectedText && selectedText.trim()) && hlBtnRef.current) {
                      const r = hlBtnRef.current.getBoundingClientRect();
                      setHlTooltipPos({ top: r.top, left: r.left });
                    }
                  }}
                  onMouseLeave={() => setHlTooltipPos(null)}
                >
                  <button
                    type="button"
                    className="question-body-action-btn question-body-action-btn--highlight"
                    disabled={!(selectedText && selectedText.trim())}
                    onClick={() => { if (selectedText && selectedText.trim()) addHighlight(); }}
                  >🖍﹢ Highlight</button>
                  {hlTooltipPos && !(selectedText && selectedText.trim()) && ReactDOM.createPortal(
                    <div
                      className="highlight-add-tooltip"
                      role="tooltip"
                      style={{ top: hlTooltipPos.top, left: hlTooltipPos.left }}
                    >
                      Select text in the PDF to attach it as a highlight
                    </div>,
                    document.body
                  )}
                </span>
              )}
              {onEdit && (
                <button type="button" className="question-body-action-btn" onClick={startEdit}>✎ Edit</button>
              )}
              {onRegenerate && (
                <button
                  type="button"
                  className="question-body-action-btn question-body-action-btn--regen"
                  onClick={!question.question?.trim() ? () => onRegenerate(index, '') : startRegen}
                >↺ Regenerate</button>
              )}
              {onRemove && (
                <button type="button" className="question-body-action-btn question-body-action-btn--remove" onClick={onRemove}>✕ Remove</button>
              )}
            </div>
          )}
          <HighlightList
            highlights={getHighlights(question)}
            aiHighlights={getAiHighlights(question)}
            onRemove={readOnly ? undefined : removeHighlight}
            onRemoveAi={readOnly ? undefined : removeAiHighlight}
          />
          {showRegen && !readOnly && (
            <div className="regen-form">
              <textarea
                className="regen-textarea"
                rows={2}
                placeholder="Optional notes for regeneration…"
                value={regenNotes}
                onChange={e => setRegenNotes(e.target.value)}
              />
              <div className="regen-actions">
                <button className="regen-cancel-btn" onClick={() => setShowRegen(false)}>Cancel</button>
                <button
                  className="regen-submit-btn"
                  onClick={() => { onRegenerate?.(index, regenNotes); setShowRegen(false); setRegenNotes(''); }}
                >
                  Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="edit-form">
          <label className="edit-label">Question</label>
          <textarea
            className="edit-textarea"
            rows={3}
            value={editDraft.question || ''}
            onChange={e => setEditDraft(d => ({ ...d, question: e.target.value }))}
          />
          {hasOptions && (
            <>
              <label className="edit-label">Options (one per line)</label>
              <textarea
                className="edit-textarea"
                rows={4}
                value={(editDraft.options || []).join('\n')}
                onChange={e => setEditDraft(d => ({ ...d, options: e.target.value.split('\n') }))}
              />
            </>
          )}
          {hasHints && (
            <>
              <label className="edit-label">Hints (one per line)</label>
              <textarea
                className="edit-textarea"
                rows={4}
                value={(editDraft.hints || []).join('\n')}
                onChange={e => setEditDraft(d => ({ ...d, hints: e.target.value.split('\n') }))}
              />
            </>
          )}
          <label className="edit-label">Answer</label>
          {editDraft.type === 'mcq' ? (
            <input
              className="edit-input"
              value={editDraft.answer || ''}
              onChange={e => setEditDraft(d => ({ ...d, answer: e.target.value }))}
            />
          ) : (
            <textarea
              className="edit-textarea"
              rows={4}
              value={editDraft.answer || ''}
              onChange={e => setEditDraft(d => ({ ...d, answer: e.target.value }))}
            />
          )}
          <div className="edit-actions">
            {onEdit && (
              <span
                ref={hlBtnRef}
                className="highlight-add-btn-wrap"
                onMouseEnter={() => {
                  if (!(selectedText && selectedText.trim()) && hlBtnRef.current) {
                    const r = hlBtnRef.current.getBoundingClientRect();
                    setHlTooltipPos({ top: r.top, left: r.left });
                  }
                }}
                onMouseLeave={() => setHlTooltipPos(null)}
              >
                <button
                  type="button"
                  className="edit-action-btn question-body-action-btn--highlight"
                  disabled={!(selectedText && selectedText.trim())}
                  onClick={() => { if (selectedText && selectedText.trim()) addHighlight(); }}
                >🖍﹢ Highlight</button>
                {hlTooltipPos && !(selectedText && selectedText.trim()) && ReactDOM.createPortal(
                  <div
                    className="highlight-add-tooltip"
                    role="tooltip"
                    style={{ top: hlTooltipPos.top, left: hlTooltipPos.left }}
                  >
                    Select text in the PDF to attach it as a highlight
                  </div>,
                  document.body
                )}
              </span>
            )}
            <button className="edit-cancel-btn" onClick={() => setEditing(false)}>Cancel</button>
            <button className="edit-save-btn" onClick={saveEdit}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CycleCard ─────────────────────────────────────────────────────────────────

export function CycleCard({ cycle, index, defaultOpen, alwaysOpen, onEdit, onRegenerate, onRegenerateSection, regenLoading, readOnly, selectedText, selectedPage, onRegenerateQuestion, onAddQuestion, regenQuestionIndex }) {
  const [open, setOpen] = useState(!!defaultOpen || !!alwaysOpen);
  const [editingSection, setEditingSection] = useState(null); // null | 'title' | 'core_ideas' | 'recommendations'
  const [editDraft, setEditDraft] = useState(null);
  const [regenSection, setRegenSection] = useState(null); // null | 'core_ideas' | 'recommendations' | 'diagrams' | 'highlights'
  const [regenNotes, setRegenNotes] = useState('');

  // Normalize diagrams: new format is cycle.diagrams [{title, code}]; legacy is cycle.mermaid (string).
  // Accept both during the transition — the LLM is instructed to emit the new format going forward.
  const diagrams = (() => {
    if (Array.isArray(cycle.diagrams)) {
      return cycle.diagrams
        .map(d => {
          if (typeof d === 'string') return { title: null, code: d };
          if (d && typeof d === 'object') return { title: d.title || null, code: d.code || '' };
          return null;
        })
        .filter(d => d && typeof d.code === 'string' && d.code.trim());
    }
    if (cycle.mermaid && typeof cycle.mermaid === 'string' && cycle.mermaid.trim()) {
      return [{ title: null, code: cycle.mermaid }];
    }
    return [];
  })();
  const hasDiagrams = diagrams.length > 0;

  const startSectionEdit = (section) => {
    setEditDraft({
      ...cycle,
      core_ideas: Array.isArray(cycle.core_ideas) ? cycle.core_ideas.join('\n') : '',
      recommendations: Array.isArray(cycle.recommendations) ? cycle.recommendations.join('\n') : '',
    });
    setEditingSection(section);
    setOpen(true);
  };

  const saveSectionEdit = () => {
    const updated = {
      ...editDraft,
      core_ideas: editDraft.core_ideas.split('\n').filter(Boolean),
      recommendations: editDraft.recommendations.split('\n').filter(Boolean),
    };
    onEdit?.(index, updated);
    setEditingSection(null);
  };

  const cancelSectionEdit = () => setEditingSection(null);

  const SECTION_REGEN_PREFIX = {
    core_ideas: 'Regenerate ONLY the core_ideas — replace them with fresh, high-quality entries. Keep all other cycle fields (title, read_sections, recommendations, diagrams, questions, ai_highlights) exactly as they are.',
    recommendations: 'Regenerate ONLY the recommendations — replace them with fresh, actionable guiding tips. Keep all other cycle fields exactly as they are.',
    diagrams: 'Regenerate ONLY the diagrams — replace them with a new diagram. Keep all other cycle fields exactly as they are.',
    highlights: 'Regenerate ONLY the ai_highlights — replace them with fresh relevant passages from the paper. Keep all other cycle fields exactly as they are.',
  };

  const startSectionRegen = (section) => {
    setRegenSection(section);
    setOpen(true);
  };

  const submitSectionRegen = () => {
    if (onRegenerateSection) {
      onRegenerateSection(index, regenSection, regenNotes);
    } else {
      const prefix = SECTION_REGEN_PREFIX[regenSection] || 'Regenerate this cycle.';
      const combined = regenNotes.trim() ? `${prefix} Additional instructions: ${regenNotes.trim()}` : prefix;
      onRegenerate?.(index, combined);
    }
    setRegenSection(null);
    setRegenNotes('');
  };

  // Append the current PDF selection to this cycle's highlights (one click).
  const addHighlight = () => {
    const t = (selectedText || '').trim();
    if (!t) return;
    const next = [...getHighlights(cycle), { page: selectedPage ?? null, text: t }];
    const { highlight, ...rest } = cycle;
    onEdit?.(index, { ...rest, highlights: next });
    setOpen(true);
  };

  const removeHighlight = (i) => {
    const next = getHighlights(cycle).filter((_, j) => j !== i);
    const { highlight, ...rest } = cycle;
    onEdit?.(index, { ...rest, highlights: next });
  };

  const removeAiHighlight = (i) => {
    const next = getAiHighlights(cycle).filter((_, j) => j !== i);
    onEdit?.(index, { ...cycle, ai_highlights: next });
  };

  // Per-question edits (text/options/answer edits AND pinned highlights) are
  // local data mutations: splice the updated question back into this cycle and
  // bubble the whole cycle up via onEdit. Per-question regenerate needs the LLM,
  // so it's delegated to onRegenerateQuestion (passed only where authoring is
  // live). When that handler is absent the question rows render read-only.
  const editQuestion = (qIndex, updatedQuestion) => {
    const questions = (cycle.questions || []).map((q, j) => (j === qIndex ? updatedQuestion : q));
    onEdit?.(index, { ...cycle, questions });
  };
  const removeQuestion = (qIdx) => {
    const questions = (cycle.questions || []).filter((_, j) => j !== qIdx);
    onEdit?.(index, { ...cycle, questions });
  };
  const addQuestion = () => {
    const existing = cycle.questions || [];
    const questions = [...existing, { question: '', hints: [], answer: null, type: 'short-answer', number: existing.length + 1 }];
    onEdit?.(index, { ...cycle, questions });
  };
  const questionsEditable = !readOnly && !!onRegenerateQuestion;

  return (
    <div className={`cycle-card${regenLoading ? ' cycle-card--loading' : ''}`}>
      <div className="cycle-header-wrap">
        <div className="cycle-card-header-static">
          <span className="cycle-number">{cycle.number ?? '?'}</span>
          <span className="cycle-title">{cycle.title}</span>
          {Array.isArray(cycle.questions) && cycle.questions.length > 0 && (
            <span className="cycle-meta">{cycle.questions.length} question{cycle.questions.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        {regenLoading && <span className="regen-spinner">⟳</span>}
        {!alwaysOpen && (
          <button
            type="button"
            className="cycle-chevron"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            aria-label={open ? 'Collapse cycle' : 'Expand cycle'}
          >{open ? '▲' : '▼'}</button>
        )}
      </div>

      {(alwaysOpen || open) && (
        <div className="cycle-card-body">

          {/* Title & Read Sections */}
          {editingSection === 'title' ? (
            <div className="cycle-section-edit-form">
              <label className="edit-label">Title</label>
              <input className="edit-input" value={editDraft.title || ''} onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))} />
              <label className="edit-label">Read sections</label>
              <input className="edit-input" value={editDraft.read_sections || ''} onChange={e => setEditDraft(d => ({ ...d, read_sections: e.target.value }))} />
              <div className="section-edit-actions">
                <button className="edit-cancel-btn" onClick={cancelSectionEdit}>Cancel</button>
                <button className="edit-save-btn" onClick={saveSectionEdit}>Save</button>
              </div>
            </div>
          ) : (cycle.read_sections || !readOnly) ? (
            <div className="cycle-section-block">
              <div className="cycle-section-header-row">
                {cycle.read_sections && (
                  <div className="cycle-read">{fixPageRanges(cycle.read_sections)}</div>
                )}
                {!readOnly && (
                  <div className="cycle-section-actions">
                    <button className="cycle-section-action-btn" title="Edit title & read sections" onClick={() => startSectionEdit('title')}>✎ Edit</button>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Highlights */}
          {(!readOnly || getHighlights(cycle).length > 0 || getAiHighlights(cycle).length > 0) && (
            <div className="cycle-section-block">
              {!readOnly && (
                <div className="cycle-section-header-row">
                  <span className="cycle-section-title">Highlights</span>
                  <div className="cycle-section-actions">
                    <button
                      className="cycle-section-action-btn cycle-section-action-btn--highlight"
                      disabled={!(selectedText && selectedText.trim())}
                      onClick={() => { if (selectedText && selectedText.trim()) addHighlight(); }}
                      title={selectedText && selectedText.trim() ? 'Add the current PDF selection as a highlight' : 'Select text in the PDF first'}
                    >🖍﹢ Add</button>
                    <button className="cycle-section-action-btn cycle-section-action-btn--regen" title="Regenerate AI highlights" onClick={() => startSectionRegen('highlights')}>↺ Regen</button>
                  </div>
                </div>
              )}
              <HighlightList
                highlights={getHighlights(cycle)}
                aiHighlights={getAiHighlights(cycle)}
                onRemove={readOnly ? undefined : removeHighlight}
                onRemoveAi={readOnly ? undefined : removeAiHighlight}
              />
              {regenSection === 'highlights' && !readOnly && (
                <div className="regen-form">
                  <textarea className="regen-textarea" rows={2} placeholder="Optional notes…" value={regenNotes} onChange={e => setRegenNotes(e.target.value)} />
                  <div className="regen-actions">
                    <button className="regen-cancel-btn" onClick={() => { setRegenSection(null); setRegenNotes(''); }}>Cancel</button>
                    <button className="regen-submit-btn" onClick={submitSectionRegen}>Regenerate</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Core Ideas */}
          {editingSection === 'core_ideas' ? (
            <div className="cycle-section-block cycle-section-edit-form">
              <label className="edit-label">Core ideas (one per line)</label>
              <textarea className="edit-textarea" rows={4} value={editDraft.core_ideas || ''} onChange={e => setEditDraft(d => ({ ...d, core_ideas: e.target.value }))} />
              <div className="section-edit-actions">
                <button className="edit-cancel-btn" onClick={cancelSectionEdit}>Cancel</button>
                <button className="edit-save-btn" onClick={saveSectionEdit}>Save</button>
              </div>
            </div>
          ) : (Array.isArray(cycle.core_ideas) && cycle.core_ideas.length > 0) || !readOnly ? (
            <div className="cycle-section-block">
              <div className="cycle-section-header-row">
                <span className="cycle-section-title">Core Ideas</span>
                {!readOnly && (
                  <div className="cycle-section-actions">
                    <button className="cycle-section-action-btn" onClick={() => startSectionEdit('core_ideas')}>✎ Edit</button>
                    <button className="cycle-section-action-btn cycle-section-action-btn--regen" onClick={() => startSectionRegen('core_ideas')}>↺ Regen</button>
                  </div>
                )}
              </div>
              {Array.isArray(cycle.core_ideas) && cycle.core_ideas.length > 0 && (
                <ol className="core-ideas-list">{cycle.core_ideas.map((idea, i) => <li key={i}>{idea}</li>)}</ol>
              )}
              {regenSection === 'core_ideas' && !readOnly && (
                <div className="regen-form">
                  <textarea className="regen-textarea" rows={2} placeholder="Optional notes…" value={regenNotes} onChange={e => setRegenNotes(e.target.value)} />
                  <div className="regen-actions">
                    <button className="regen-cancel-btn" onClick={() => { setRegenSection(null); setRegenNotes(''); }}>Cancel</button>
                    <button className="regen-submit-btn" onClick={submitSectionRegen}>Regenerate</button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Guiding Recommendations */}
          {editingSection === 'recommendations' ? (
            <div className="cycle-section-block cycle-section-edit-form">
              <label className="edit-label">Recommendations (one per line)</label>
              <textarea className="edit-textarea" rows={3} value={editDraft.recommendations || ''} onChange={e => setEditDraft(d => ({ ...d, recommendations: e.target.value }))} />
              <div className="section-edit-actions">
                <button className="edit-cancel-btn" onClick={cancelSectionEdit}>Cancel</button>
                <button className="edit-save-btn" onClick={saveSectionEdit}>Save</button>
              </div>
            </div>
          ) : (Array.isArray(cycle.recommendations) && cycle.recommendations.length > 0) || !readOnly ? (
            <div className="cycle-section-block">
              <div className="cycle-section-header-row">
                <span className="cycle-section-title">Guiding Recommendations</span>
                {!readOnly && (
                  <div className="cycle-section-actions">
                    <button className="cycle-section-action-btn" onClick={() => startSectionEdit('recommendations')}>✎ Edit</button>
                    <button className="cycle-section-action-btn cycle-section-action-btn--regen" onClick={() => startSectionRegen('recommendations')}>↺ Regen</button>
                  </div>
                )}
              </div>
              {Array.isArray(cycle.recommendations) && cycle.recommendations.length > 0 && (
                <ul className="recommendations-list">{cycle.recommendations.map((rec, i) => <li key={i}>{rec}</li>)}</ul>
              )}
              {regenSection === 'recommendations' && !readOnly && (
                <div className="regen-form">
                  <textarea className="regen-textarea" rows={2} placeholder="Optional notes…" value={regenNotes} onChange={e => setRegenNotes(e.target.value)} />
                  <div className="regen-actions">
                    <button className="regen-cancel-btn" onClick={() => { setRegenSection(null); setRegenNotes(''); }}>Cancel</button>
                    <button className="regen-submit-btn" onClick={submitSectionRegen}>Regenerate</button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Diagrams */}
          {(hasDiagrams || !readOnly) && (
            <div className="cycle-section-block">
              <div className="cycle-section-header-row">
                <span className="cycle-section-title">
                  {diagrams.length > 1 ? `Diagrams (${diagrams.length})` : 'Diagram'}
                </span>
                {!readOnly && (
                  <div className="cycle-section-actions">
                    <button className="cycle-section-action-btn cycle-section-action-btn--regen" onClick={() => startSectionRegen('diagrams')}>↺ Regen</button>
                  </div>
                )}
              </div>
              {diagrams.map((d, i) => (
                <div key={i} className="cycle-diagram-block">
                  {d.title && <div className="cycle-diagram-title">{d.title}</div>}
                  <MermaidDiagram code={d.code} />
                </div>
              ))}
              {regenSection === 'diagrams' && !readOnly && (
                <div className="regen-form">
                  <textarea className="regen-textarea" rows={2} placeholder="Optional notes…" value={regenNotes} onChange={e => setRegenNotes(e.target.value)} />
                  <div className="regen-actions">
                    <button className="regen-cancel-btn" onClick={() => { setRegenSection(null); setRegenNotes(''); }}>Cancel</button>
                    <button className="regen-submit-btn" onClick={submitSectionRegen}>Regenerate</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Questions */}
          {(Array.isArray(cycle.questions) && cycle.questions.length > 0 || !readOnly) && (
            <div className="cycle-section-block">
              <div className="cycle-section-title">Questions</div>
              {(cycle.questions || []).map((q, i) => (
                <QuestionRow
                  key={i}
                  question={q}
                  index={i}
                  showTypeBadge={false}
                  readOnly={!questionsEditable}
                  onEdit={questionsEditable ? editQuestion : undefined}
                  onRegenerate={questionsEditable ? ((qi, notes) => onRegenerateQuestion(index, qi, notes)) : undefined}
                  onRemove={!readOnly ? () => removeQuestion(i) : undefined}
                  actionsInBody={!readOnly}
                  regenLoading={regenQuestionIndex === i}
                  selectedText={selectedText}
                  selectedPage={selectedPage}
                />
              ))}
              {!readOnly && (
                <button
                  className="add-question-btn"
                  disabled={regenQuestionIndex != null}
                  onClick={() => (onAddQuestion ? onAddQuestion(index) : addQuestion())}
                >
                  {onAddQuestion ? '✦ Add question (PaperBuddy generates it)' : '+ Add Question'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ── CyclesPlan ────────────────────────────────────────────────────────────────

function CyclesPlan({ plan, onEditSection, onRegenerateSection, onRegenerateQuestion, regenIndex, regenQuestionKey, readOnly, selectedText, selectedPage }) {
  return (
    <>
      <div className="plan-header">
        <div className="plan-title">Reading Cycles Plan Final Preview</div>
      </div>
      <div className="plan-content">
        {Array.isArray(plan.cycles) && plan.cycles.map((cycle, i) => (
          <CycleCard
            key={i}
            cycle={cycle}
            index={i}
            defaultOpen={false}
            onEdit={onEditSection}
            onRegenerate={onRegenerateSection}
            regenLoading={regenIndex === i}
            readOnly={readOnly}
            selectedText={selectedText}
            selectedPage={selectedPage}
            onRegenerateQuestion={onRegenerateQuestion}
            regenQuestionIndex={regenQuestionKey && regenQuestionKey.cycleIndex === i ? regenQuestionKey.qIndex : null}
          />
        ))}
      </div>
    </>
  );
}

// ── TestingPlan ───────────────────────────────────────────────────────────────

function TestingPlan({ plan, onEditSection, onRegenerateSection, onReorderSection, onRemoveSection, regenIndex, readOnly, selectedText, selectedPage }) {
  // Drag-to-reorder. Native HTML5 DnD (no extra dependency): the grip handle is
  // the draggable trigger, the whole row is the drop target, and dropping moves
  // the dragged question to the target's position via onReorderSection.
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (e, i) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(i)); } catch {}
    // Drag the whole card, not just the grip the pointer grabbed.
    const row = e.currentTarget.closest('.question-reorder-row');
    if (row) { try { e.dataTransfer.setDragImage(row, 24, 20); } catch {} }
  };

  const handleDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const handleDrop = (targetIndex) => {
    if (dragIndex !== null && dragIndex !== targetIndex) onReorderSection?.(dragIndex, targetIndex);
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <>
      <div className="plan-header">
        <div className="plan-title">Test Plan Final Preview</div>
        {plan.is_bank && (
          <div className="plan-bank-note">
            🎲 Question bank — {Array.isArray(plan.questions) ? plan.questions.length : '?'} questions in the pool; each student is randomly given {plan.sample_size}.
          </div>
        )}
      </div>
      <div className="plan-content">
        {Array.isArray(plan.questions) && plan.questions.map((q, i) => {
          const rowClass = 'question-reorder-row'
            + (dragIndex === i ? ' question-reorder-row--dragging' : '')
            + (overIndex === i && dragIndex !== null && dragIndex !== i ? ' question-reorder-row--drop-target' : '');
          return (
            <div
              key={i}
              className={rowClass}
              onDragOver={readOnly ? undefined : (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (overIndex !== i) setOverIndex(i);
              }}
              onDrop={readOnly ? undefined : (e) => { e.preventDefault(); handleDrop(i); }}
            >
              {!readOnly && (
                <div
                  className="drag-handle"
                  draggable
                  tabIndex={0}
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragEnd={handleDragEnd}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); onReorderSection?.(i, i - 1); }
                    else if (e.key === 'ArrowDown' && i < plan.questions.length - 1) { e.preventDefault(); onReorderSection?.(i, i + 1); }
                  }}
                  role="button"
                  aria-label={`Reorder question ${i + 1}. Drag to move, or press the up and down arrow keys.`}
                  title="Drag to reorder (or focus and press ↑ / ↓)"
                >⠿</div>
              )}
              <QuestionRow
                question={q}
                index={i}
                showTypeBadge={true}
                actionsInBody
                onEdit={onEditSection}
                onRegenerate={onRegenerateSection}
                onRemove={onRemoveSection ? () => onRemoveSection(i) : undefined}
                regenLoading={regenIndex === i}
                readOnly={readOnly}
                selectedText={selectedText}
                selectedPage={selectedPage}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── LessonPlanPreview (main export) ───────────────────────────────────────────

export default function LessonPlanPreview({
  plan,
  paperName,
  introMessage,
  aiReply,
  approvalNotes,
  onApprovalNotesChange,
  dueDate,
  onDueDateChange,
  planName = '',
  onPlanNameChange,
  onApprove,
  onRequestChanges,
  onEditSection,
  onRegenerateSection,
  onRegenerateQuestion,
  onReorderSection,
  onRemoveSection,
  regenIndex,
  regenQuestionKey,
  loading,
  planSaved = false,
  readOnly = false,
  hideApproval = false,
  hideExport = false,
  selectedText,
  selectedPage,
}) {
  if (!plan) return null;
  const planType = plan.plan_type;

  return (
    <div className={`lesson-plan-preview${planType === 'testing' ? ' lesson-plan-preview--testing' : ''}`}>
      {/* Export toolbar */}
      {!hideExport && <div className="plan-export-bar">
        <span className="plan-export-label">Export:</span>
        <button className="plan-export-btn" onClick={() => exportPdf(plan, paperName)}>📄 PDF</button>
        <button className="plan-export-btn" onClick={() => exportWord(plan, paperName)}>📝 Word</button>
        <button className="plan-export-btn" onClick={() => exportJson(plan, paperName)}>&#123;&#125; JSON</button>
      </div>}

      <div className="plan-scroll-area">
        {planType === 'cycles' && (
          <CyclesPlan
            plan={plan}
            onEditSection={readOnly ? undefined : onEditSection}
            onRegenerateSection={readOnly ? undefined : onRegenerateSection}
            onRegenerateQuestion={readOnly ? undefined : onRegenerateQuestion}
            regenIndex={regenIndex}
            regenQuestionKey={regenQuestionKey}
            readOnly={readOnly}
            selectedText={selectedText}
            selectedPage={selectedPage}
          />
        )}
        {planType === 'testing' && (
          <TestingPlan
            plan={plan}
            onEditSection={readOnly ? undefined : onEditSection}
            onRegenerateSection={readOnly ? undefined : onRegenerateSection}
            onReorderSection={readOnly ? undefined : onReorderSection}
            onRemoveSection={readOnly ? undefined : onRemoveSection}
            regenIndex={regenIndex}
            readOnly={readOnly}
            selectedText={selectedText}
            selectedPage={selectedPage}
          />
        )}
        {planType !== 'cycles' && planType !== 'testing' && (
          <div className="plan-header">
            <div className="plan-title">Lesson Plan</div>
            {introMessage && <div className="plan-intro-message">{introMessage}</div>}
          </div>
        )}
      </div>

      {!readOnly && !hideApproval && aiReply && (
        <div className="plan-ai-reply">
          <div className="plan-ai-reply-label">AI Response</div>
          <div className="plan-ai-reply-text">{aiReply}</div>
        </div>
      )}

      {!readOnly && !hideApproval && (
        <div className="plan-approval-bar">
          <p className="plan-approval-label">Does this look good?</p>
          <input
            className="plan-name-input"
            type="text"
            placeholder="Give this plan a name (optional)…"
            value={planName}
            onChange={e => onPlanNameChange?.(e.target.value)}
            disabled={loading}
          />
          <textarea
            className="plan-approval-textarea"
            rows={1}
            placeholder="Optional: describe any changes…"
            value={approvalNotes}
            onChange={e => onApprovalNotesChange(e.target.value)}
            disabled={loading}
          />
          <div className="plan-due-date-row">
            <label className="plan-due-date-label" htmlFor="plan-due-date">Due date (optional)</label>
            <input
              id="plan-due-date"
              type="date"
              className="plan-due-date-input"
              value={dueDate || ''}
              min={new Date().toISOString().slice(0, 10)}
              onChange={e => onDueDateChange?.(e.target.value)}
              disabled={loading}
            />
            {dueDate && dueDate < new Date().toISOString().slice(0, 10) && (
              <span className="plan-due-date-error">Due date cannot be in the past</span>
            )}
          </div>
          <div className="plan-approval-actions">
            <button className="plan-approve-btn" onClick={() => onApprove(plan)} disabled={loading || planSaved || !!(dueDate && dueDate < new Date().toISOString().slice(0, 10))}>
              {planSaved ? '✓ Saved' : 'Approve & Save'}
            </button>
            <button
              className="plan-changes-btn"
              onClick={() => onRequestChanges(approvalNotes)}
              disabled={loading || !approvalNotes || !approvalNotes.trim()}
            >
              Request changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
